<template>
  <div class="page">
    <div class="pl-8 p-6 mt-5 pb-8">
      <span class="note-text">
        Important Note
      </span>

      <p class="important-note-desc mt-6">
        Please note that based on the campaign type and objective, which you chose in step 1, some sections may vary.
      </p>
    </div>

    <div class="divider"></div>

    <div class="pl-8 p-6 pt-8">
      <h3 class="font-normal">
        Create New Audience
      </h3>

      <p class="create-audience-desc mt-1">
        You can customize the characteristics of the audience you want to target. You have to fill at least 1 section to create your new audience.
      </p>

      <div class="vx-col md:w-1/2 w-full my-6">
        <speakol-input
          label="Audience Name"
          id="targeting-audiences-audience-name"
          placeholder="Audience Name"
          v-model="audience.name"
          validation="max:25"
          :maxLength="25"
        >
        </speakol-input>

        <span v-if="$vErrors.has([errors], `name`)" class="text-danger">
          {{ $vErrors.first([errors], `name`) }}
        </span>
      </div>
    </div>

    <vs-collapse ref="createAudienceCollapse" class="p-0 bg-white" accordion>
      <div class="divider"></div>
      <vs-collapse-item class="sm:p-4">
        <div class="font-normal text-2xl" slot="header" v-bind:class="{ 'text-danger': hasError, 'text-black': !hasError }">
          {{ $t('Location Targeting') }}
          <p class="section-desc  text-gray-500 font-normal">
            {{ $t("You can  target audience from all around the world. The Sky is the limit.") }}
          </p>
        </div>

        <LocationTargeting v-model="audience.locations" v-on:input="handleChangeEvent"/>
      </vs-collapse-item>

      <div class="divider"></div>

      <vs-collapse-item class="sm:p-4">
        <div class="font-normal text-2xl" slot="header" v-bind:class="{ 'text-danger': hasError, 'text-black': !hasError }">
          {{ $t('Keywords Targeting') }}
          <p class="section-desc text-gray-500 font-normal">
            {{ $t("Please fill in your targetted keywords.") }}
          </p>
        </div>

        <div class="keywords-note">
          <span>
            <vs-icon class="warning-icon" style="font-size: 25px;" icon="speakol-warning" icon-pack="speakol"></vs-icon>
            Please note that this section will appear only when you create an inline campaign.
          </span>
        </div>

        <Keywords v-model="audience.keywords" v-on:input="handleChangeEvent" />
      </vs-collapse-item>

      <div class="divider"></div>

      <vs-collapse-item class="sm:p-4">
        <div class="font-normal text-2xl" slot="header" v-bind:class="{ 'text-danger': hasError, 'text-black': !hasError }">
          {{ $t('Publishers') }}
          <p class="section-desc  text-gray-500 font-normal">
            {{ $t("Target some of the regions most premium websites.") }}
          </p>
        </div>

        <TargetPlatforms
          v-model="audience.target_platforms"
          :industry_id="0"
          :account_id="this.active_account_id"
          :editing="false"
          :adsetIndex="0"
          v-on:input="handleChangeEvent"
        />
      </vs-collapse-item>

      <div class="divider" v-if="$hasFeature('content-categories')"></div>

      <vs-collapse-item class="sm:p-4" v-if="$hasFeature('content-categories')">
        <div class="font-normal text-2xl" slot="header" v-bind:class="{ 'text-danger': hasError, 'text-black': !hasError }">
          {{ $t('Categories') }}
          <p class="section-desc  text-gray-500 font-normal">
            {{ $t("Target the categories you would like to feature your ads alongside. ") }}
          </p>
        </div>

        <ContentCategories
          v-model="audience.content_categories"
          :industry_id="0"
          :editing="false"
          :adsetIndex="0"
          v-on:input="handleChangeEvent"
        />
      </vs-collapse-item>

      <div class="divider"></div>

      <vs-collapse-item class="sm:p-4">
        <div class="font-normal text-2xl" slot="header" v-bind:class="{ 'text-danger': hasError, 'text-black': !hasError }">
          {{ $t('Targeting Devices') }}
          <p class="section-desc text-gray-500 font-normal">
            {{ $t("Reach audience across all platforms.") }}
          </p>
        </div>

        <TargetDevices v-model="audience.target_devices" :adsetIndex="0" v-on:input="handleChangeEvent"/>
      </vs-collapse-item>

      <div class="divider"></div>

      <vs-collapse-item class="sm:p-4 specific-class">
        <div class="font-normal text-2xl" slot="header" v-bind:class="{ 'text-danger': hasError, 'text-black': !hasError }">
          {{ $t('Advanced Options') }}
          <p class="section-desc text-gray-500 font-normal">
            {{ $t("Specify the number of times you want you ad to be displayed.") }}
          </p>
        </div>

        <AdvancedOptions v-model="audience" :advertising-channel="defaultAdvertisingChannel" :adsetIndex="0" v-on:input="handleChangeEvent"/>
      </vs-collapse-item>
    </vs-collapse>

    <div class="p-6 pt-0 pl-8">
      <vs-button id="targeting-audiences-save-button"  class="btn mb-2 shadow-none" @click="saveAudience">Save Audience</vs-button>
      <span class="p-4 text-danger" v-if="hasError && $vErrors.hasMany([errors], ['audience_errors'])">
        <ul>
          <li v-for="name in ['audience_errors']" :key="name">
            {{ $vErrors.first([errors], name) }}
          </li>
        </ul>
      </span>
    </div>

    <!-- popup -->
    <speakol-response-popup
      :responsePopupData="responsePopupData"
      @hide-response-popup="goToAudienceTable()"
    />
  </div>
</template>

<script>
import TargetPlatforms from '@/views/campaigns/components/steps/components/AdsetContent/components/TargetPlatforms.vue'
import AdvancedOptions from '@/views/campaigns/components/steps/components/AdsetContent/components/AdvancedOptions.vue'
import TargetDevices from '@/views/campaigns/components/steps/components/AdsetContent/components/TargetDevices.vue'
import Keywords from '@/views/campaigns/components/steps/components/AdsetContent/components/Keywords.vue'
import LocationTargeting from '@/views/campaigns/components/steps/components/AdsetContent/components/LocationTargeting.vue'
import ContentCategories from '@/views/campaigns/components/steps/components/AdsetContent/components/ContentCategories.vue'

export default {
  components: {
    TargetPlatforms,
    AdvancedOptions,
    TargetDevices,
    Keywords,
    LocationTargeting,
    ContentCategories
  },
  data () {
    return {
      responsePopupData: {
        show: false,
        type: 'success',
        body: 'Your audience has been successfully saved.'
      },
      hasError: false,
      audience: {
        id: 0,
        name: '',
        locations: {
          countries: [],
          cities: []
        },
        content_categories: [],
        isps: [],
        keywords: [],
        target_devices: [1, 2, 3],
        target_platforms: {
          websites: [],
          apps: [],
          include_new_platforms: true
        },
        frequency_capping: 0
      }
    }
  },

  async beforeCreate () {
    if (this.$store.getters['campaign/targetingLookups'].length === 0) {
      await this.$store.dispatch('campaign/getTargetingLookups')
    }
  },

  beforeDestroy () {
    if (this.$refs.createAudienceCollapse.$children.length) {
      for (const item of this.$refs.createAudienceCollapse.$children) {
        window.removeEventListener('resize', item.changeHeight)
      }
    }
  },
  computed: {
    active_account_id () {
      return parseInt(localStorage.getItem('activeAccountId'))
    },
    defaultAdvertisingChannel () {
      // Speakol will be the only available advertising channel for create audience targeting until other channels are enabled.
      return this.$store.getters['campaign/targetingLookups'].advertising_channels.find((channel) => channel.id === this.$constants.CAMPAIGN_ADVERTISING_CHANNELS_IDS.SPEAKOL);
    }
  },
  methods: {
    showSuccessPopup () {
      this.responsePopupData.show = true
    },
    hideSuccessPopup () {
      this.responsePopupData.show = false
    },
    goToAudienceTable () {
      this.hideSuccessPopup()

      setTimeout(() => {
        this.$router.push('/targeting/audiences')
      }, 0)
    },
    handleChangeEvent () {
      this.hasError = false
    },
    async saveAudience () {
      try {
        this.hasError = false
        await window.axios.post('/audience/create', this.audience)
        this.showSuccessPopup()
      } catch (err) {
        this.errors.clear()

        if ('name' in err.response.data.errors) {
          this.errors.add({
            field: 'name',
            msg: err.response.data.errors.name.join(', ')
          })
        }

        if ('locations.countries' in err.response.data.errors) {
          this.hasError = true

          this.errors.add({
            field: 'audience_errors',
            msg: 'You have to fill at least 1 section to create your new audience.'
          })
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  .page::v-deep {
    .section-desc {
      font-style: normal;
      font-weight: normal;
      font-size: 16px;
      line-height: 24px;
      color: #999;
    }

    .vs-collapse-item {
      border-bottom: 0;
    }

    .create-audience-desc {
      color: #999;
    }

    .keywords-note {
      background: #fffbe6;
      border: 1px solid #ffe58f;
      border-radius: 4px;
      padding: 9px;
      margin-bottom: 35px;

      span {
        font-weight: normal;
        font-size: 14px;
        color: rgba(0, 0, 0, 0.65);
      }

      i {
        color: #faad14;
        font-size: 16px !important;
        position: relative;
        top: 3px;
        margin-right: 8px;
        margin-left: 10px;
      }
    }

    .note-text {
      padding: 10px;
      font-weight: normal;
      font-size: 16px;
      color: #e02020;
      background: #fce9e9;
      border-radius: 5px;
    }

    .specific-class {
      border-bottom-right-radius: 15px;
      border-bottom-left-radius: 15px;

      .tree-children {
        li {
          .tree-content {
            padding-left: 25px !important;
          }
        }
      }
    }

    .section-desc {
      margin-top: 4px;
      font-size: 14px;
    }

    .specific-class.open-item .vs-collapse-item--content {
      overflow: visible !important;
    }

    .divider {
      width: 95%;
      border: 1px solid rgba(189, 195, 202, 0.215527);
      margin: 0 auto;
    }

    .adsetSectionContainer {
      .vs-collapse-item {
        border-bottom: 0 !important;
      }

      .ad-set-details-container {
        padding-top: 20px;
        border-bottom-right-radius: 15px;
        border-bottom-left-radius: 15px;
      }
    }

    .audience-section-container {
      border-top-left-radius: 15px;
      border-top-right-radius: 15px;
    }

    .save-audience-button {
      button {
        background-color: white;
        border: 1px solid #0092d7;
        color: #0092d7;
        box-shadow: none;
        width: 180px;
        height: 40px;
        cursor: pointer;
        margin-left: 30px;
        border-radius: 3px;
      }

      .save-audience-desc-parent {
        margin-top: 20px;
        margin-left: 30px;
        padding-bottom: 30px;

        .save-audience-desc {
          color: #0092d7;
          font-weight: 300;
          font-size: 14px;
        }
      }
    }

    .adsetSectionContainer header {
      cursor: pointer;
    }

    .adsetSectionContainer header.vs-collapse-item--header span i {
      font-size: 3rem;
    }

    .adsetSectionContainer header.vs-collapse-item--header div {
      font-size: 1.5rem;
    }

    .adsetSectionContainer .vs-collapse-item {
      background-color: white;
      cursor: default;
    }

    .adsetSectionContainer .vx-card .vx-card__header .vx-card__actions svg {
      height: 4rem;
      width: 4rem;
    }

    .treeContainer {
      border: 1px solid rgba(0, 0, 0, 0.15);
      border-radius: 10px;
    }

    @media (max-width: 576px) {
      .adsetSectionContainer header.vs-collapse-item--header div {
        font-size: 1.2rem;
      }

      .vuesax-app-is-ltr .vs-collapse.default .open-item .vs-collapse-item--icon-header {
        margin-top: -43px;
      }

      .adsetSectionContainer header.vs-collapse-item--header span i {
        font-size: 2rem;
      }
    }

    /* For tags span */
    .span-tag {
      border: 1px solid #6dc5d0;
      box-sizing: border-box;
      border-radius: 21px;
      padding: 6px 10px;
      font-weight: 500;
      color: #6dc5d0;
      cursor: pointer;
    }

    .span-tag:hover {
      background-color: rgba(109, 197, 208, 0.15);
    }

    .span-tag-active {
      background-color: #6dc5d0;
      color: white;
    }

    .span-tag-active:hover {
      background-color: #6dc5d0;
    }

    @media (max-width: 576px) {
      .span-tag {
        overflow-x: scroll;
        display: inline-block;
      }

      .section-desc {
        width: 90%;
      }
    }

    /* For the checkbox tree */
    .tree-checkbox.checked,
    .tree-checkbox.indeterminate {
      border: 2px solid #6dc5d0 !important;
      background: #6dc5d0;
      border-radius: 2px !important;
      width: 16px;
      height: 16px;
    }

    .tree-checkbox {
      width: 16px !important;
      border-radius: 2px !important;
      height: 16px !important;
    }

    .tree-checkbox.checked::after {
      left: 4px !important;
      top: -1px !important;
      height: 9px !important;
      width: 4px !important;
    }

    .tree-node:not(.selected) > .tree-content:hover {
      background-color: #e9f6f8;
    }

    .tree-node.selected > .tree-content {
      background: #e9f6f8;
    }

    .tree-content {
      padding-left: 0 !important;
    }

    .first-collapse-style {
      border-top-left-radius: 0;
      border-top-right-radius: 0;
      border-bottom-right-radius: 15px;
      border-bottom-left-radius: 15px;
    }

    .collapse-style {
      border-radius: 15px;
    }

    .card-error {
      border: 1px solid rgba(224, 32, 32, 0.5);
    }

    .divider {
      border: 1px solid rgba(189, 195, 202, 0.215527);
      margin: 0 auto;
    }

    .btn {
      background: #0092d7;
      border: 1.28px solid #fff;
      box-sizing: border-box;
      border-radius: 2.88px;
    }

    .autocomplete-input,
    .autocomplete-input:focus,
    .autocomplete-input[aria-expanded=true] {
      background-color: white;
      border: 1px solid rgba(0, 0, 0, 0.15);
      box-shadow: none;
      padding: 7px 7px 7px 48px;
      border-radius: 4px;
    }
  }
</style>
